import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { executeGameAction } from "api/game/gameActions";
import { GameContext } from "components/pages/game/GamePage";
import { CardType } from "models/CardType";
import { useSnackbar } from "notistack";
import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { actionIdToDescription } from "utils/action_helpers";
import {
  canUseDirigibles,
  canUseHeat,
  canUseSteel,
  canUseTitanium,
  getNumberOfDirigibles,
  hasFundingOptions,
} from "utils/funding_helpers";

interface ActionData {
  milestone?: string;
  award?: string;
  tapOption?: number;
  colonyTileName?: string;
}

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  ActionId: string;
  ActionData?: ActionData;
  DecisionId: string;
  description?: string;
  card?: CardType;
  content?: React.ReactNode;
  costsMoney?: boolean;
}

export const Component: React.FC<Props> = ({
  isOpen,
  ActionId,
  ActionData = {},
  DecisionId,
  description,
  onClose,
  card,
  content,
  costsMoney = true,
}) => {
  const { game } = useContext(GameContext);
  const { player } = game;
  const { gameId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [titanium, setTitanium] = useState<number>(0);
  const [steel, setSteel] = useState<number>(0);
  const [heat, setHeat] = useState<number>(0);
  const [dirigibles, setDirigibles] = useState<number>(0);

  const onConfirm = () => {
    if (gameId === undefined) {
      console.error("Game ID is undefined");
      return;
    }

    let additionalActionData: {
      heat?: number;
      titanium?: number;
      steel?: number;
      dirigibles?: number;
      card?: string;
    } = {};
    if (heat > 0) {
      additionalActionData.heat = heat;
    }
    if (titanium > 0) {
      additionalActionData.titanium = titanium;
    }
    if (steel > 0) {
      additionalActionData.steel = steel;
    }
    if (dirigibles > 0) {
      additionalActionData.dirigibles = dirigibles;
    }
    if (card) {
      additionalActionData.card = card.name;
    }

    executeGameAction({
      gameId,
      ActionData: {
        ...ActionData,
        ...additionalActionData,
      },
      ActionId,
      DecisionId,
      enqueueSnackbar,
    }).then(() => {
      onClose();
    });
  };

  const actionText = () => {
    switch (ActionId) {
      case "PLAY_CARD":
        return card?.name ?? "Unknown Card";
      case "TAP_CARD":
        return description;
      case "CLAIM_MILESTONE":
        return description;
      case "FUND_AWARD":
        return description;

      default:
        return actionIdToDescription(ActionId);
    }
  };

  const renderSlider = ({
    name,
    currentAmount,
    max,
    setValue,
  }: {
    name: string;
    currentAmount: number;
    max: number;
    setValue: (x: number) => void;
  }) => {
    return (
      <Box sx={{ mt: 2, width: "100%", padding: "24px", boxSizing: "border-box" }}>
        <Typography>
          {name} (<em>{max} total</em>)
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={currentAmount}
          onChange={(e) => {
            setValue(Number(e.target.value));
          }}
          InputProps={{
            inputProps: { min: 0, max: max },
          }}
          margin="dense"
        />
        <Slider
          value={currentAmount}
          onChange={(e, newValue) => setValue(newValue as number)}
          aria-labelledby="input-slider"
          min={0}
          max={max}
          valueLabelDisplay="auto"
        />
      </Box>
    );
  };

  const renderSliders = () => {
    return (
      <>
        {canUseHeat(player)
          ? renderSlider({
              name: "Heat",
              currentAmount: heat,
              max: player.resources.heat,
              setValue: setHeat,
            })
          : null}
        {canUseTitanium({ player, card })
          ? renderSlider({
              name: "Titanium",
              currentAmount: titanium,
              max: player.resources.titanium,
              setValue: setTitanium,
            })
          : null}
        {canUseSteel({ player, card })
          ? renderSlider({
              name: "Steel",
              currentAmount: steel,
              max: player.resources.steel,
              setValue: setSteel,
            })
          : null}
        {canUseDirigibles({ player, card })
          ? renderSlider({
              name: "Dirigibles",
              currentAmount: dirigibles,
              max: getNumberOfDirigibles(player),
              setValue: setDirigibles,
            })
          : null}
      </>
    );
  };

  const renderFundingSelectionModal = () => {
    return (
      <Dialog open={isOpen} onClose={onClose} aria-labelledby="fund-action-modal">
        <DialogTitle id="fund-aciton-modal">Choose Funding Method</DialogTitle>
        <DialogActions sx={{ flexDirection: "column", display: "flex" }}>
          <Typography>
            Select how you would like to pay for <strong>{actionText()}</strong>
          </Typography>
          {renderSliders()}
          <Typography>
            <em>Any remaining balance will be paid for in money.</em>
          </Typography>
          <Box>
            <Button onClick={onClose} color="secondary">
              Decline
            </Button>
            <Button onClick={() => onConfirm()} color="primary">
              Confirm
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  };

  const renderConfirmationModal = () => {
    const finalContent = content ?? (
      <Typography>
        Confirm you want to play <strong>{actionText()}</strong>
      </Typography>
    );
    return (
      <Dialog open={isOpen} onClose={onClose} aria-labelledby="confirm-decision-title">
        <DialogTitle id="confirm-decision-title">Confirm Decision</DialogTitle>
        <DialogContent>{finalContent}</DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Decline
          </Button>
          <Button onClick={() => onConfirm()} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return costsMoney && hasFundingOptions({ player, card })
    ? renderFundingSelectionModal()
    : renderConfirmationModal();
};

Component.displayName = "FundActionModal";
